import React, { useEffect } from 'react'
import { getUserContainer } from '../../container/user-module'
import { USER_API_KEY } from '../../modules/users'
import { IUserApi } from '../../modules/users/api/UserApi'
import { LOGGED_USER_SERVICE_KEY } from '../../modules/users'
import { ILoggedUserService } from '../../modules/users/services/LoggedUserService'
import { STATUS_SERVICE_KEY } from '../../container/app'
import { IStatusService } from 'common/status/StatusService'

export const AppleCallbackHandler: React.FC = () => {
  useEffect(() => {
    if (window.location.pathname === '/auth/apple/callback') {
      const userContainer = getUserContainer()
      const userApi = userContainer.get<IUserApi>(USER_API_KEY)
      const loggedUserService = userContainer.get<ILoggedUserService>(LOGGED_USER_SERVICE_KEY)
      const statusService = userContainer.get<IStatusService>(STATUS_SERVICE_KEY)

      const processCallback = async () => {
        let lastError: any = null

        const statusSub = statusService.statusObservable().subscribe(async (status: any) => {
          if (status.variant === 'error' && status.error) {
            if (status.error instanceof Promise) {
              try {
                lastError = await status.error
              } catch (e) {
                lastError = e
              }
            } else {
              lastError = status.error
            }
          }
        })

        try {
          const urlParams = new URLSearchParams(window.location.search)
          const provider = 'apple'
          const code = urlParams.get('code')
          const state = urlParams.get('state')

          if (!code) {
            throw new Error('MISSING_PARAMETERS')
          }

          const operation = state?.startsWith('registration:') ? 'registration' : 'login'
          const registrationToken = state?.startsWith('registration:')
            ? state.replace('registration:', '')
            : state || ''

          let result

          if (operation === 'login') {
            result = await new Promise((resolve, reject) => {
              const subscription = loggedUserService
                .loginWithOAuth(code, state ?? undefined, provider)
                .subscribe({
                  next: (value) => {
                    if (value) {
                      resolve(value)
                      subscription.unsubscribe()
                    } else {
                      setTimeout(() => {
                        const errorToUse = lastError || new Error('USER_NOT_FOUND')
                        reject(errorToUse)
                        subscription.unsubscribe()
                      }, 200)
                    }
                  },
                  error: async (error) => {
                    let resolvedError = error
                    if (error instanceof Promise) {
                      try {
                        resolvedError = await error
                      } catch (e) {
                        resolvedError = e
                      }
                    }

                    const errorMessage = resolvedError.message || resolvedError.toString() || ''

                    if (errorMessage.includes('USER_NOT_FOUND')) {
                      reject(new Error('USER_NOT_FOUND'))
                    } else {
                      reject(resolvedError)
                    }
                    subscription.unsubscribe()
                  },
                })
            })
          } else {
            result = await new Promise((resolve, reject) => {
              const subscription = userApi
                .registerWithOAuth(provider, code, registrationToken)
                .subscribe({
                  next: (value) => {
                    if (value) {
                      resolve(value)
                      subscription.unsubscribe()
                    } else {
                      setTimeout(() => {
                        const errorToUse = lastError || new Error('REGISTRATION_FAILED')
                        reject(errorToUse)
                        subscription.unsubscribe()
                      }, 200)
                    }
                  },
                  error: async (error) => {
                    let resolvedError = error
                    if (error instanceof Promise) {
                      try {
                        resolvedError = await error
                      } catch (e) {
                        resolvedError = e
                      }
                    }
                    reject(resolvedError)
                    subscription.unsubscribe()
                  },
                })
            })
          }

          statusSub.unsubscribe()

          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'APPLE_OAUTH_SUCCESS',
                user: result,
                operation: operation,
              },
              window.location.origin
            )
          }
        } catch (error: any) {
          statusSub.unsubscribe()

          let errorCode = 'UNKNOWN_ERROR'
          let errorMessage = 'Error en la autenticación con Apple'
          let originalError = 'Unknown error'

          if (error) {
            const errorMsg = error.message || error.error || error.toString() || ''
            originalError = errorMsg
            const errorString = errorMsg.toLowerCase()

            if (errorString.includes('user_not_found') || errorString === 'user_not_found') {
              errorCode = 'USER_NOT_FOUND'
              errorMessage = 'Usuario no encontrado. Por favor, regístrate primero.'
            } else if (errorString.includes('email_already_exists')) {
              errorCode = 'EMAIL_ALREADY_EXISTS'
              errorMessage = 'El email ya está registrado'
            } else if (errorString.includes('invalid_password')) {
              errorCode = 'INVALID_PASSWORD'
              errorMessage = 'Contraseña incorrecta'
            } else if (errorString.includes('apple_auth_failed')) {
              errorCode = 'APPLE_AUTH_FAILED'
              errorMessage = 'Error al autenticar con Apple'
            } else if (errorString) {
              errorCode = errorString.toUpperCase()
              errorMessage = errorMsg
            }
          }

          if (window.opener) {
            window.opener.postMessage(
              {
                type: 'APPLE_OAUTH_ERROR',
                error: errorMessage,
                errorCode: errorCode,
                originalError: originalError,
              },
              window.location.origin
            )
          }
        } finally {
          setTimeout(() => {
            window.close()
          }, 100)
        }
      }

      processCallback()
    }
  }, [])

  return null
}
